if (!customElements.get('alto-product-lightbox')) {
  customElements.define(
    'alto-product-lightbox',
    class AltoProductLightbox extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.zoomed = false;
        this._returnFocus = null;
        this._onKeyDown = this._handleKeyDown.bind(this);
      }

      connectedCallback() {
        this.closeBtn = this.querySelector('[data-lightbox-close]');
        this.mainImage = this.querySelector('[data-lightbox-image]');
        this.spinner = this.querySelector('[data-lightbox-spinner]');
        this.thumbs = Array.from(this.querySelectorAll('[data-lightbox-thumb]'));

        this.closeBtn.addEventListener('click', () => this.close());
        this.mainImage.addEventListener('click', () => this.toggleZoom());
        this.mainImage.addEventListener('load', () => this._setLoading(false));
        this.mainImage.addEventListener('error', () => this._setLoading(false));

        this.thumbs.forEach((thumb, i) => {
          thumb.addEventListener('click', () => {
            this.resetZoom();
            this.goTo(i);
          });
        });

        document.addEventListener('alto:lightbox-open', (e) => {
          if (e.detail.sectionId !== this.dataset.section) return;
          this._returnFocus = e.detail.trigger;
          const index = this.thumbs.findIndex(
            (t) => t.dataset.mediaId === e.detail.mediaId
          );
          this.open(index >= 0 ? index : 0);
        });
      }

      open(index) {
        this.goTo(index);
        this.removeAttribute('hidden');
        document.addEventListener('keydown', this._onKeyDown);
        document.body.style.overflow = 'hidden';
        // Use Dawn's existing trapFocus from global.js
        if (typeof trapFocus === 'function') {
          trapFocus(this.querySelector('[role="dialog"]'), this.closeBtn);
        } else {
          this.closeBtn.focus();
        }
      }

      close() {
        this.setAttribute('hidden', '');
        document.removeEventListener('keydown', this._onKeyDown);
        this.resetZoom();
        document.body.style.overflow = '';
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this._returnFocus);
        } else if (this._returnFocus) {
          this._returnFocus.focus();
        }
      }

      goTo(index) {
        this.currentIndex = (index + this.thumbs.length) % this.thumbs.length;
        const thumb = this.thumbs[this.currentIndex];

        this._setLoading(true);
        this.mainImage.src = thumb.dataset.src;
        this.mainImage.alt = thumb.dataset.alt;
        // Cached images don't fire 'load' — clear synchronously so the spinner never flashes
        if (this.mainImage.complete) this._setLoading(false);

        this.thumbs.forEach((t, i) => {
          const active = i === this.currentIndex;
          t.classList.toggle('tw:opacity-100', active);
          t.classList.toggle('tw:opacity-40', !active);
          t.classList.toggle('tw:border-neutral-400', active);
          t.classList.toggle('tw:border-neutral-200', !active);
          t.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      _setLoading(isLoading) {
        this.mainImage.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        // Hide the previous image while the new one loads so only the spinner shows
        this.mainImage.classList.toggle('tw:opacity-0', isLoading);
        if (this.spinner) this.spinner.classList.toggle('tw:hidden', !isLoading);
      }

      toggleZoom() {
        this.zoomed = !this.zoomed;
        this.mainImage.style.transform = this.zoomed ? 'scale(2)' : '';
        this.mainImage.style.cursor = this.zoomed ? 'zoom-out' : 'zoom-in';
      }

      resetZoom() {
        this.zoomed = false;
        this.mainImage.style.transform = '';
        this.mainImage.style.cursor = '';
      }

      _handleKeyDown(e) {
        switch (e.key) {
          case 'Escape':
            this.close();
            break;
          case 'ArrowLeft':
            this.resetZoom();
            this.goTo(this.currentIndex - 1);
            break;
          case 'ArrowRight':
            this.resetZoom();
            this.goTo(this.currentIndex + 1);
            break;
        }
      }
    }
  );
}

// Wire up gallery trigger buttons after DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Per-slide loading spinners in the main gallery
  document.querySelectorAll('media-gallery [role="list"] > li').forEach((slide) => {
    const img = slide.querySelector('img');
    const spinner = slide.querySelector('[data-media-spinner]');
    if (!img || !spinner) return;
    const hide = () => spinner.classList.add('tw:hidden');
    if (img.complete && img.naturalWidth > 0) {
      hide();
    } else {
      spinner.classList.remove('tw:hidden');
      img.addEventListener('load', hide);
      img.addEventListener('error', hide);
    }
  });

  document.querySelectorAll('[data-lightbox-trigger]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const gallery = btn.closest('media-gallery');
      const sectionId = gallery ? gallery.id.replace('MediaGallery-', '') : null;
      if (!sectionId) return;
      document.dispatchEvent(
        new CustomEvent('alto:lightbox-open', {
          detail: {
            sectionId,
            mediaId: btn.dataset.mediaId,
            trigger: btn,
          },
        })
      );
    });
  });
});
